import request from '@teetsh/app/src/utils/request';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LivretEval } from '@teetsh/app/src/containers/Livrets/types/livret';
import livretKeys from '@teetsh/app/src/containers/Livrets/queries/keys';
import { updateMatiereLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/updateMatiereLivretEleve';
import { updateCompetence } from '@teetsh/app/src/containers/Livrets/queries/competences/updateCompetence';
import { useTranslation } from 'react-i18next';
import { useLSUErrorsMapping } from 'containers/Livrets/useLSUErrorsMapping';

export interface UpdateLivretEleveCompetenceDTO {
  id: string;
  matiereId: string;
  domaineId?: string;
  name: string;
  note?: number;
}

function updateLivretEleveCompetence({
  schoolyearId,
  schoolId,
  livretEleveId,
  competence,
}: {
  schoolyearId: string;
  schoolId: string;
  livretEleveId: string;
  competence: UpdateLivretEleveCompetenceDTO;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets-eleves/${livretEleveId}/competences/${competence.id}`;
  return request(requestURL, {
    method: 'PATCH',
    body: competence,
  });
}

interface Props {
  livretEleveId: string;
  livretEvalId: string;
}

export function useUpdateLivretCompetence(props: Props) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const errorsMapping = useLSUErrorsMapping();
  const { livretEleveId, livretEvalId } = props;
  const queryClient = useQueryClient();
  const { t } = useTranslation(['carnet']);
  const { mutateAsync } = useMutation({
    mutationFn: (competence: UpdateLivretEleveCompetenceDTO) =>
      updateLivretEleveCompetence({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        livretEleveId,
        competence,
      }),
    onMutate: async competence => {
      await queryClient.cancelQueries({
        queryKey: livretKeys.livretDetail(
          schoolyear.id,
          school.id,
          livretEvalId,
        ),
      });
      const previousLivretEval = queryClient.getQueryData(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
      );
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (oldLivretEval: LivretEval) => ({
          ...oldLivretEval,
          livretsEleves: (oldLivretEval?.livretsEleves || []).map(
            livretEleve => {
              if (livretEleve.id === livretEleveId) {
                const matiere = livretEleve.matieres.find(
                  m => m.id === competence.matiereId,
                );
                if (!matiere) return livretEleve;
                const domaine = competence?.domaineId
                  ? matiere.domaines.find(d => d.id === competence.domaineId)
                  : undefined;
                const newMatiere = updateCompetence(
                  matiere,
                  {
                    name: competence.name,
                    note: competence.note,
                    id: competence.id,
                  },
                  domaine,
                );
                return updateMatiereLivretEleve(
                  livretEleve,
                  newMatiere,
                  oldLivretEval.typePositionnement,
                  errorsMapping,
                );
              }
              return livretEleve;
            },
          ),
        }),
      );
      return { previousLivretEval };
    },
    onError: () => {
      notifyError({
        title: t('carnet:messages.competence.updateError'),
      });
    },
  });
  return mutateAsync;
}
